import React, { useState, useEffect } from 'react';
import { DeltaBadge } from './DeltaBadge';

// Brand colors — matched to ContentHealthTable for consistent visual hierarchy
const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  grey: '#e76f51',
  green: '#01796f'
};

const CampaignScorecard = ({ campaign, apiBaseUrl }) => {
  const [metrics, setMetrics] = useState({
    visibility: null,
    citation: null,
    position: null,
    content: null,
    technical: null,
    entityGap: null,
    knowledgeGraph: null
  });
  const [deltas, setDeltas] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (campaign?.id) {
      fetchMetrics();
    }
  }, [campaign?.id]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const [llmData, urlData, contentGraphData, knowledgeGraphData, summaryData] = await Promise.all([
        fetchLLMMetrics(),
        fetchURLMetrics(),
        fetchContentGraphMetrics(),
        fetchKnowledgeGraphMetrics(),
        fetchAuditSummary()
      ]);

      setMetrics({
        visibility: llmData.visibility,
        citation: llmData.citation,
        position: llmData.position,
        content: urlData.content,
        technical: urlData.technical,
        entityGap: contentGraphData.score,
        knowledgeGraph: knowledgeGraphData.score
      });
      setDeltas(summaryData);
    } catch (err) {
      console.error('Error fetching scorecard metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditSummary = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/campaigns/${campaign.id}/audit-summary`);
      if (!response.ok) return {};
      const json = await response.json();
      if (!json.success) return {};
      return json.modules || {};
    } catch (err) {
      console.error('Error fetching audit summary:', err);
      return {};
    }
  };

  const fetchLLMMetrics = async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/audit-results?campaignId=${campaign.id}&moduleType=llm-audit&limit=1`
      );
      if (!response.ok) return { visibility: null, citation: null, position: null };
      const results = await response.json();
      if (!results || results.length === 0) return { visibility: null, citation: null, position: null };

      const data = typeof results[0].result_data === 'string'
        ? JSON.parse(results[0].result_data)
        : results[0].result_data;

      // ─── Defensive shape resolution ───
      // Two writers populate llm-audit results with slightly different shapes:
      //   1. test-single-campaign.js (manual)  → data.results = { results: [...], brandMetrics: [...] }
      //   2. scheduled cron job                 → data.results = [...] and data.summary.brandMetrics = [...]
      // Resolve both into a flat array of prompt results and a flat brandMetrics array.
      const promptResults = Array.isArray(data.results)
        ? data.results
        : Array.isArray(data.results?.results)
          ? data.results.results
          : [];
      const brandMetrics = data.summary?.brandMetrics
        || data.results?.brandMetrics
        || [];
      const targetBrand = brandMetrics.find(b => b.isTarget);

      // ─── CITATION RATE: domain-matching logic ───
      // Identical methodology to CitationsDashboard.js.
      // Of responses that include citations, % where our domain appears as a source.
      // (NOT brand mention rate — that was the previous bug.)
      const brandDomains = [...new Set((campaign.urls || []).map(u => {
        const urlStr = typeof u === 'string' ? u : u?.url || '';
        try {
          return new URL(urlStr).hostname.replace(/^www\./, '').toLowerCase();
        } catch {
          return null;
        }
      }).filter(Boolean))];

      let totalWithCitations = 0;
      let totalBrandCited = 0;
      promptResults.forEach(promptResult => {
        const responses = (promptResult.responses || []).filter(r => !r.error);
        const withCitations = responses.filter(r => r.citations?.length > 0);
        totalWithCitations += withCitations.length;
        totalBrandCited += withCitations.filter(r => {
          return r.citations.some(citation => {
            const citationUrl = typeof citation === 'string'
              ? citation
              : (citation.url || citation.source || '');
            return brandDomains.some(domain => citationUrl.toLowerCase().includes(domain));
          });
        }).length;
      });

      const citationRate = totalWithCitations > 0
        ? Math.round((totalBrandCited / totalWithCitations) * 100)
        : 0;

      console.log(
        `[CampaignScorecard] Citation: ${totalBrandCited}/${totalWithCitations} = ${citationRate}% | Domains: ${brandDomains.join(', ')}`
      );

      return {
        visibility: targetBrand?.visibilityScore ?? null,
        citation: citationRate,
        position: targetBrand?.averagePosition ?? null
      };
    } catch (err) {
      console.error('Error fetching LLM metrics:', err);
      return { visibility: null, citation: null, position: null };
    }
  };

  const fetchURLMetrics = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/modules/overview/${campaign.id}`);
      if (!response.ok) return { content: null, technical: null };
      const data = await response.json();
      if (!data.success || !data.data?.results) return { content: null, technical: null };

      const results = data.data.results;
      if (results.length === 0) return { content: null, technical: null };

      // Content: average of Structured, Depth (contentComprehensiveness), Answers across all URLs
      const contentScore = Math.round(
        results.reduce((sum, r) => {
          const avg = ((r.structuredContent || 0) + (r.contentComprehensiveness || 0) + (r.answerDirectness || 0)) / 3;
          return sum + avg;
        }, 0) / results.length
      );

      // Technical: average of Schema, Authors, Freshness, SVO across all URLs
      const technicalScore = Math.round(
        results.reduce((sum, r) => {
          const avg = ((r.schema || 0) + (r.authors || 0) + (r.freshness || 0) + (r.svo || 0)) / 4;
          return sum + avg;
        }, 0) / results.length
      );

      return { content: contentScore, technical: technicalScore };
    } catch (err) {
      console.error('Error fetching URL metrics:', err);
      return { content: null, technical: null };
    }
  };

  const fetchContentGraphMetrics = async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/audit-results?campaignId=${campaign.id}&moduleType=content-graph&limit=1`
      );
      if (!response.ok) return { score: null };
      const results = await response.json();
      if (!results || results.length === 0) return { score: null };

      const data = typeof results[0].result_data === 'string'
        ? JSON.parse(results[0].result_data)
        : results[0].result_data;

      const gapMatrix = data.gapMatrix?.matrix || [];
      const allEntities = gapMatrix;  // Include all entities (no competitor filtering)

      if (allEntities.length === 0) return { score: null };

      const matched = allEntities.filter(e => (e.urlsCovering || 0) > 0);
      const missing = allEntities.filter(e => (e.urlsCovering || 0) === 0);
      const matchedPct = (matched.length / allEntities.length) * 100;
      const missingPct = (missing.length / allEntities.length) * 100;

      const yourAvgSalience = matched.length > 0
        ? matched.reduce((sum, e) => sum + (e.avgSupply || 0), 0) / matched.length
        : 0;

      const llmAvgSalience = allEntities.length > 0
        ? allEntities.reduce((sum, e) => sum + (e.promptSalience || 0), 0) / allEntities.length
        : 0;

      const emphasisScore = llmAvgSalience > 0
        ? Math.min(100, (yourAvgSalience / llmAvgSalience) * 100)
        : 0;

      const healthScore = Math.round(
        (matchedPct * 0.4) + (emphasisScore * 0.3) + ((100 - missingPct) * 0.3)
      );

      return { score: healthScore };
    } catch (err) {
      console.error('Error fetching content graph metrics:', err);
      return { score: null };
    }
  };

  const fetchKnowledgeGraphMetrics = async () => {
    try {
      // FIXED (Jun 2026): module_type changed from 'knowledge-graph-audit' to
      // 'knowledge-graph' as part of the entity-rollup rewrite (see
      // KnowledgeGraphDashboard2.js / api/modules/knowledge-graph-audit.js).
      // Querying the old module_type returned zero rows for every campaign,
      // so this tile always showed N/A regardless of whether a KG audit had
      // actually run. Score now comes from rolledUpKGIS (with the legacy
      // summary.avgKGIS field as a fallback — they're the same value, just
      // named differently depending on which writer produced the row).
      const response = await fetch(
        `${apiBaseUrl}/audit-results?campaignId=${campaign.id}&moduleType=knowledge-graph&limit=1`
      );
      if (!response.ok) return { score: null };
      const results = await response.json();
      if (!results || results.length === 0) return { score: null };

      const data = typeof results[0].result_data === 'string'
        ? JSON.parse(results[0].result_data)
        : results[0].result_data;

      const kgisScore = data.rolledUpKGIS ?? data.summary?.avgKGIS ?? null;
      return { score: kgisScore };
    } catch (err) {
      console.error('Error fetching knowledge graph metrics:', err);
      return { score: null };
    }
  };

  const getScoreColor = (score, type = 'default') => {
    if (score === null) return colors.primary;
    if (type === 'position') {
      // Position: lower is better (1.0 is perfect rank)
      if (score <= 2) return colors.green;
      if (score <= 5) return colors.secondary;
      if (score <= 8) return colors.tertiary;
      return colors.grey;
    }
    // All other metrics: higher is better
    // Thresholds match ContentHealthTable band() function for consistency
    if (score >= 70) return colors.green;
    if (score >= 45) return colors.tertiary;
    return colors.grey;
  };

  const formatMetricValue = (value, type) => {
    if (value === null) return 'N/A';
    switch (type) {
      case 'visibility':
      case 'citation':
      case 'content':
      case 'technical':
      case 'entityGap':
      case 'knowledgeGraph':
        return `${Math.round(value)}%`;
      case 'position':
        return value.toFixed(1);
      default:
        return value;
    }
  };

  if (loading) {
    return (
      <div
        className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3"
        style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
      >
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm">Loading metrics...</span>
        </div>
      </div>
    );
  }

  const metricItems = [
    { key: 'visibility', label: 'VISIBILITY', type: 'visibility', deltaKey: 'llm-audit' },
    { key: 'citation', label: 'CITATION', type: 'citation', deltaKey: 'llm-audit-citation' },
    { key: 'position', label: 'POSITION', type: 'position', deltaKey: null },
    { key: 'content', label: 'CONTENT', type: 'content', deltaKey: null },
    { key: 'technical', label: 'TECHNICAL', type: 'technical', deltaKey: 'schema-audit' },
    { key: 'entityGap', label: 'ENTITY GAP', type: 'entityGap', deltaKey: 'content-graph', invertColors: true },
    { key: 'knowledgeGraph', label: 'KNOWLEDGE GRAPH', type: 'knowledgeGraph', deltaKey: 'knowledge-graph' }
  ];

  return (
    <div
      className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4"
      style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
    >
      <div className="max-w-7xl">
        <div className="mb-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Campaign Overview
          </h2>
        </div>
        <div className="flex items-end gap-6 w-full">
          {metricItems.map(({ key, label, type, deltaKey, invertColors }) => (
            <div key={key} className="flex flex-col items-start">
              <div
                className="text-3xl font-bold mb-1"
                style={{ color: getScoreColor(metrics[key], type) }}
              >
                {formatMetricValue(metrics[key], type)}
              </div>
              <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
              <DeltaBadge
                delta={deltaKey ? (deltas[deltaKey]?.delta ?? null) : null}
                suffix="%"
                invertColors={invertColors}
              />
            </div>
          ))}
          {/* Last Updated */}
          <div className="ml-auto flex flex-col items-end self-end">
            <div className="text-xs text-gray-400">Last updated</div>
            <div className="text-xs font-medium text-gray-600">
              {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignScorecard;
